import request from '@/axios'
import { useUserStoreWithOut } from '@/store/modules/user'
import type { UserType } from '@/api/login/types'

/**
 * 前端日志与埋点上报（阿里云 SLS Web Tracking）。
 *
 * 与 App 对齐的两点：
 * 1. **同一个 project / logstore**：目标同样向后端 `GET /log/sts_token` 询问
 *    （Web Tracking 不需要凭证，只取 endpoint/project/logstore），因此前端无需维护
 *    SLS 配置，换环境跟着 baseUrl 走。
 * 2. **同一套字段结构**：`level` / `message` / `timestamp` / `env` / `user_id` + 自定义属性，
 *    与 `AliyunLogService._buildLogPayload` 保持一致，两端数据可以在同一个 Logstore 里一起查。
 *    事件同样以 `message = "sendEvent: {event}"` 的 info 日志落库（并额外带 `event` 字段）。
 *
 * 为什么用 Web Tracking 而不是像 App 那样 STS + PutLogs：
 * PutLogs 要 protobuf 编码 + HMAC 签名 + Content-MD5，且**签名头导致无法使用 sendBeacon**——
 * 而"点击后立即跳转"的埋点正需要 sendBeacon 才不丢。Web Tracking 是阿里云面向网页的官方通道。
 * ⚠️ 前提：该 Logstore 需在 SLS 控制台开启「Web Tracking（匿名写入）」。
 *
 * 行为约定：fire-and-forget，不 await、不抛错，任何失败都不影响业务流程。
 */

interface SlsTarget {
  endpoint: string
  project: string
  logstore: string
}

type LogLevel = 'info' | 'warning' | 'error'

let trackUrl = ''
/** 地址就绪前的日志先入队，拿到地址后补发，避免丢掉首屏事件 */
let pending: string[] = []
let resolving: Promise<void> | null = null
let disabled = false

const resolveTrackUrl = (): Promise<void> => {
  if (resolving) return resolving
  resolving = request
    .get<IResponse<SlsTarget>>({ url: '/log/sts_token' })
    .then((res) => {
      const d = (res as unknown as IResponse<SlsTarget>)?.data
      if (!d?.endpoint || !d?.project || !d?.logstore) {
        disabled = true
        return
      }
      trackUrl = `https://${d.project}.${d.endpoint}/logstores/${d.logstore}/track?APIVersion=0.6.0`
      pending.splice(0).forEach(post)
    })
    .catch(() => {
      // 后端未配置 SLS 或请求失败 → 关闭上报、不再重试
      disabled = true
      pending = []
    })
  return resolving
}

/** 登录用户标识，与 App 的 user_id（mid）对齐 */
const userFields = (): Record<string, string> => {
  try {
    const info = useUserStoreWithOut().getUserInfo as (UserType & { mid?: number }) | undefined
    if (info?.mid) return { user_id: String(info.mid) }
  } catch {
    /* store 尚未初始化时忽略 */
  }
  return {}
}

/** Web 端上下文，对应 App 的 device/app 信息 */
const contextFields = (): Record<string, string> => ({
  env: import.meta.env.VITE_NODE_ENV ?? '',
  platform: 'web',
  channel: 'web',
  app_version: import.meta.env.VITE_APP_VERSION ?? '',
  url: window.location.pathname, // 只取 path，避免 query 里的敏感参数进日志
  ua: navigator.userAgent
})

/** SLS 字段值必须是字符串 */
const stringify = (props: Record<string, unknown>): Record<string, string> => {
  const out: Record<string, string> = {}
  Object.keys(props).forEach((k) => {
    const v = props[k]
    if (v === undefined || v === null) return
    out[k] = typeof v === 'string' ? v : String(v)
  })
  return out
}

/**
 * Content-Type 必须用 text/plain。
 *
 * CORS 的「简单请求」只允许 text/plain、multipart/form-data、application/x-www-form-urlencoded；
 * 用 application/json 会触发 OPTIONS 预检，而 SLS track 接口不应答预检 → 浏览器直接报 CORS error。
 * 声明成 text/plain 不影响服务端解析（SLS 按 body 内容解析 JSON）。
 */
const CONTENT_TYPE = 'text/plain;charset=UTF-8'

const post = (body: string) => {
  try {
    // sendBeacon 在页面卸载时仍会发出，适合"点击后立刻跳转"的埋点
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: CONTENT_TYPE })
      if (navigator.sendBeacon(trackUrl, blob)) return
    }
    void fetch(trackUrl, {
      method: 'POST',
      body,
      keepalive: true,
      headers: { 'Content-Type': CONTENT_TYPE }
    }).catch((e) => {
      // 上报失败不影响业务；开发期打一条，便于排查（如 Logstore 未开启 Web Tracking 会表现为 CORS error）
      if (import.meta.env.DEV) console.warn('[tracker] 上报失败', e)
    })
  } catch {
    /* 上报失败不影响业务 */
  }
}

/**
 * 写一条日志到 SLS。
 *
 * **不写 `__time__`**：日志时间以阿里云 SLS 接收时刻为准。浏览器时钟可能被用户改乱，
 * 拿它当日志时间会导致时间线错乱、按时间排查失真；SLS 侧时间可信。
 * 代价是控制台会带纳秒位显示（`2026-08-02 21:04:54.175249982`），与 App 的秒级短格式不同——
 * 这是显示差异，不影响查询与排序。
 * 另附 ISO 格式 `timestamp` 字段（客户端时刻，与 App 同名），需要对照两者时可用。
 */
const writeLog = (level: LogLevel, message: string, props: Record<string, unknown> = {}) => {
  if (disabled) return
  const body = JSON.stringify({
    __topic__: 'web',
    __logs__: [
      {
        level,
        message,
        timestamp: new Date().toISOString(),
        ...contextFields(),
        ...userFields(),
        ...stringify(props)
      }
    ]
  })
  if (trackUrl) {
    post(body)
    return
  }
  // 地址还没拿到：先缓冲（上限防止异常情况下无限堆积），并触发一次解析
  if (pending.length < 50) pending.push(body)
  void resolveTrackUrl()
}

export const logInfo = (message: string, props?: Record<string, unknown>) =>
  writeLog('info', message, props)

export const logWarning = (message: string, props?: Record<string, unknown>) =>
  writeLog('warning', message, props)

export const logError = (message: string, props?: Record<string, unknown>) =>
  writeLog('error', message, props)

// ─────────────────── 防洪水 ───────────────────
// 异常常常是连发的（渲染循环报错、轮询接口一直失败），不加限制会瞬间刷爆 Logstore
// 并拖慢页面。这里做两层：相同错误短时间内只报一次 + 单次会话总量封顶。

const DEDUP_WINDOW_MS = 10_000
const MAX_ERRORS_PER_SESSION = 100
const recentErrors = new Map<string, number>()
let errorCount = 0

/** 返回 true 表示应当上报；false 表示被去重或已超上限 */
const allowError = (signature: string): boolean => {
  if (errorCount >= MAX_ERRORS_PER_SESSION) return false
  const now = Date.now()
  const last = recentErrors.get(signature)
  if (last && now - last < DEDUP_WINDOW_MS) return false
  recentErrors.set(signature, now)
  // 控制 Map 体积，避免长会话下无限增长
  if (recentErrors.size > 200) recentErrors.clear()
  errorCount++
  return true
}

// ─────────────────── 噪音过滤 ───────────────────
// 浏览器自身产生、与应用无关的报错，在进入去重与上报之前直接丢弃。
// 放在这里而不是 window.error 回调里，是为了让所有上报入口（js_error /
// unhandled_rejection / api_request_error）共用同一份名单。

const IGNORED_ERROR_PATTERNS = [
  // "ResizeObserver loop completed with undelivered notifications."（Chrome）
  // "ResizeObserver loop limit exceeded"（旧版 Chrome / Firefox）
  //
  // ResizeObserver 回调里又改动了布局时，浏览器无法在同一帧内派发完剩余通知，
  // 于是把它作为 ErrorEvent 从 window.onerror 抛出。这是规范内行为，页面功能不受影响
  // （特征是 e.error 为 undefined，因此 stack 为空），但会被全局钩子当成 js_error 上报。
  // 实测 2026-08-22 ~ 09-22 期间，它占了生产站全部 error 的 18/19，真正的异常反被淹没。
  /ResizeObserver loop/
]

/** 该条异常是否属于可忽略的浏览器噪音 */
const isIgnorableError = (props: Record<string, unknown>): boolean => {
  const msg = props.error_message
  return typeof msg === 'string' && IGNORED_ERROR_PATTERNS.some((re) => re.test(msg))
}

/** 带去重的异常上报，供全局钩子与 API 层使用 */
export const logErrorDedup = (message: string, props: Record<string, unknown> = {}) => {
  if (isIgnorableError(props)) return
  if (!allowError(`${message}|${props.path ?? ''}|${props.source ?? ''}`)) return
  logError(message, props)
}

const truncate = (v: unknown, max = 1000): string => {
  const s = typeof v === 'string' ? v : String(v ?? '')
  return s.length > max ? `${s.slice(0, max)}...(truncated)` : s
}

/**
 * 安装全局异常捕获。在 main.ts 尽早调用。
 *
 * 覆盖三类前端异常：
 * - JS 运行时未捕获异常（window.error）
 * - 未处理的 Promise 拒绝（unhandledrejection）
 * - 静态资源加载失败（图片/脚本/样式，捕获阶段监听）
 *
 * Vue 组件内部异常由 `app.config.errorHandler` 单独上报（见 main.ts）。
 */
export const setupGlobalTracking = () => {
  window.addEventListener(
    'error',
    (e: ErrorEvent) => {
      // 资源加载失败：target 是具体元素，且没有 message
      const target = e.target as HTMLElement | null
      if (target && target !== (window as unknown as HTMLElement) && target.tagName) {
        const src = (target as HTMLImageElement).src || (target as HTMLLinkElement).href || ''
        // 上报目标自身失败不再记录，否则会自我循环
        if (src && trackUrl && src.startsWith(trackUrl.split('?')[0])) return
        logErrorDedup('resource_error', {
          tag: target.tagName.toLowerCase(),
          source: truncate(src, 300)
        })
        return
      }
      logErrorDedup('js_error', {
        error_message: truncate(e.message, 300),
        source: truncate(`${e.filename}:${e.lineno}:${e.colno}`, 300),
        stack: truncate(e.error?.stack)
      })
    },
    true // 捕获阶段，资源加载错误不冒泡
  )

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason
    logErrorDedup('unhandled_rejection', {
      error_message: truncate(reason?.message ?? reason, 300),
      stack: truncate(reason?.stack)
    })
  })
}

/**
 * 上报一个埋点事件。
 * 落库形态与 App 一致（`message = "sendEvent: {event}"` 的 info 日志），
 * 同时带独立的 `event` 字段，便于在 SLS 里直接按事件聚合。
 * @param event 事件名，snake_case，与 App 命名保持一致（如 register_entry_click）
 */
export const sendEvent = (event: string, props: Record<string, unknown> = {}) =>
  writeLog('info', `sendEvent: ${event}`, { event, ...props })
